import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Navigation2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useContactInfo } from "@/context/SiteContentContext";
import { Reveal, SectionTag } from "./Reveal";
import { fetchSettings } from "@/lib/api";

// Default embed centered on Karrada, Baghdad, Iraq. Admin can override via settings.
const DEFAULT_EMBED =
  "https://www.google.com/maps?q=Karrada%2C+Baghdad%2C+Iraq&t=&z=15&ie=UTF8&iwloc=&output=embed";
const DIRECTIONS_URL = "https://www.google.com/maps/dir/?api=1&destination=Karrada%2C+Baghdad%2C+Iraq";

export default function LocationMap({ variant = "full" }) {
  const { t, lang } = useLang();
  const CONTACT_INFO = useContactInfo();
  const [settings, setSettings] = useState({});

  useEffect(() => {
    fetchSettings().then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  const embedUrl = settings.map_embed_url || DEFAULT_EMBED;
  const directionsUrl = settings.map_directions_url || DIRECTIONS_URL;
  const address = settings[`address_${lang}`] || settings.address_ar || t.map.defaultAddress;
  const hours = settings[`hours_${lang}`] || settings.hours_ar || t.map.defaultHours;

  if (variant === "mini") {
    return (
      <div className="card-surface rounded-2xl overflow-hidden" data-testid="footer-map-mini">
        <div className="relative aspect-[16/9] w-full">
          <iframe
            title="Madinaty location"
            src={embedUrl}
            className="absolute inset-0 h-full w-full grayscale-[35%] contrast-110"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg)" }}
          />
        </div>
        <div className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-300 min-w-0">
            <MapPin size={16} className="text-primary shrink-0" />
            <span className="truncate">{address}</span>
          </div>
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-[#04120a]"
            style={{ background: "var(--primary)" }}
            data-testid="footer-map-directions"
          >
            <Navigation2 size={13} /> {t.map.directions}
          </a>
        </div>
      </div>
    );
  }

  const details = [
    { icon: MapPin, label: t.map.addressLabel, value: address, dir: null },
    { icon: Clock, label: t.map.hoursLabel, value: hours, dir: null },
    { icon: Phone, label: t.contact.info.phone, value: CONTACT_INFO.phone, href: `tel:${CONTACT_INFO.phone}`, dir: "ltr" },
    { icon: Mail, label: t.contact.info.email, value: CONTACT_INFO.email, href: `mailto:${CONTACT_INFO.email}`, dir: "ltr" },
  ];

  return (
    <section id="location" className="relative section-pad overflow-hidden" data-testid="location-section">
      <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 20% 30%, rgba(0,242,96,0.08), transparent 55%)" }} />
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <div className="text-center mb-12">
          <Reveal><SectionTag>{t.map.tag}</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5">{t.map.title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-400 text-base sm:text-lg mt-4 max-w-2xl mx-auto">{t.map.subtitle}</p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Map */}
          <Reveal delay={0.1} className="lg:col-span-3">
            <motion.div
              whileHover={{ y: -4 }}
              className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_90px_-30px_rgba(0,242,96,0.35)]"
              data-testid="location-map-frame"
            >
              <div className="absolute -inset-1 opacity-40 pointer-events-none" style={{ background: "conic-gradient(from 90deg, rgba(0,242,96,0.35), transparent 40%, rgba(0,242,96,0.35))", filter: "blur(30px)" }} />
              <div className="relative aspect-[4/3] sm:aspect-[16/11]">
                <iframe
                  title="Madinaty office location"
                  src={embedUrl}
                  className="absolute inset-0 h-full w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.9)" }}
                />
                <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/10" />
              </div>
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-[#04120a] shadow-lg"
                style={{ background: "var(--primary)" }}
                data-testid="location-directions-btn"
              >
                <Navigation2 size={16} /> {t.map.directions}
              </a>
            </motion.div>
          </Reveal>

          {/* Details card */}
          <Reveal delay={0.2} className="lg:col-span-2">
            <div className="card-surface rounded-3xl p-7 sm:p-8 h-full flex flex-col" data-testid="location-details-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-11 w-11 rounded-2xl grid place-items-center" style={{ background: "rgba(0,242,96,0.12)" }}>
                  <MapPin size={20} className="text-primary" />
                </div>
                <h3 className="text-xl font-extrabold">{t.map.cardTitle}</h3>
              </div>

              <div className="space-y-5 flex-1">
                {details.map((d, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-xl grid place-items-center shrink-0 mt-0.5 border border-white/10">
                      <d.icon size={16} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-slate-500 mb-0.5">{d.label}</div>
                      {d.href ? (
                        <a href={d.href} dir={d.dir || undefined} className="font-semibold hover:text-primary transition-colors break-words" data-testid={`location-detail-${i}`}>{d.value}</a>
                      ) : (
                        <div dir={d.dir || undefined} className="font-semibold leading-relaxed break-words" data-testid={`location-detail-${i}`}>{d.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-7 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold border border-white/15 hover:border-[color:var(--primary)] hover:text-primary transition-colors"
                data-testid="location-open-maps"
              >
                <Navigation2 size={16} /> {t.map.openMaps}
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
